<template>
    <div class="cycle-report" :class="reportColorClass">
        <print-page-header :is-loading="!treatment || !patient" />

        <div v-if="treatment && patient" class="print_content portrait">
            <report-page-template
                v-for="(page, pageIndex) in pages"
                :key="pageIndex"
                :report-title="reportName"
                :page-num="pageIndex + 1"
                :total-pages="totalPages"
                @onReportTitleChange="setNewReportName"
            >
                <!-- first page only -->
                <template v-if="pageIndex === 0">
                    <report-id
                        :patient-id="patientId"
                        :treatment-id="treatmentId"
                        :treatment-start-date="treatment.startDate"
                    />
                    <cycle-overview-form :wells="wells" />
                </template>

                <div class="embryo-development">
                    <!-- first page only -->
                    <div v-if="pageIndex === 0" class="title">
                        {{ $translate('embryo_development') }}
                    </div>

                    <!-- split to many pages -->
                    <ul class="embryo-development-list">
                        <li v-for="(well, idx) in page.wells" :key="idx">
                            <embryo-development-card
                                :well="well"
                                :show-eq-score="showEqScore"
                                @eqScoreToggled="toggleEqScore"
                            />
                        </li>
                    </ul>
                </div>

                <!-- last page only -->
                <score-explained v-if="showEqScore && isLastPage(pageIndex)" />

                <closing-part v-if="isLastPage(pageIndex) && !closingNeedsExtraPage" />
            </report-page-template>

            <report-page-template
                v-if="closingNeedsExtraPage"
                :report-title="reportName"
                :page-num="totalPages"
                :total-pages="totalPages"
                class="printed-notes"
            >
                <closing-part />
            </report-page-template>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { WELL_STATUS } from '../../../business/factory/DataModel'
import UIUtils from '../../../business/utils/UIUtils'
import PrintPageHeader from '../../components/print/PrintPageHeader'
import ReportPageTemplate from '../../components/report/ReportPageTemplate'
import ReportId from '../../components/report/ReportId'
import CycleOverviewForm from '../../components/report/cycle-report/CycleOverviewForm'
import EmbryoDevelopmentCard from '../../components/report/cycle-report/EmbryoDevelopmentCard'
import ScoreExplained from '../../components/report/cycle-report/ScoreExplained'
import ClosingPart from '../../components/report/cycle-report/ClosingPart'

const PAGE_BODY = 1172 // without fda-disclaimer 1125

const REPORT_ID = 71
const CYCLE_OVERVIEW = 317
const EMBRYO_DEV_TITLE = 37

const AVAILABLE_HEIGHT_BY_PAGE = {
    firstPage: PAGE_BODY - (REPORT_ID + CYCLE_OVERVIEW + EMBRYO_DEV_TITLE),
    default: PAGE_BODY
}
const SCORE_EXPLAINED = 36
const SIGNATURES_MIN_HEIGHT = 175

const { TRANSFERRED, FROZEN, DISCARDED, THAWED, PENDING } = WELL_STATUS

const CARD_HEIGHT = {
    [TRANSFERRED]: 222,
    [FROZEN]: 222,
    [THAWED]: 222,
    [PENDING]: 222,
    [DISCARDED]: 60
}

export default {
    name: 'Report',
    components: {
        ReportPageTemplate,
        ScoreExplained,
        PrintPageHeader,
        ReportId,
        CycleOverviewForm,
        EmbryoDevelopmentCard,
        ClosingPart
    },
    data: () => ({
        showEqScore: false,
        reportName: ''
    }),
    computed: {
        ...mapState({
            treatment: state => state.timelapse.selectedTreatment,
            patient: state => state.timelapse.selectedPatient,
            tenant: state => state.common.selectedTenant
        }),
        wells() {
            if (!this.treatment) return []

            const { TRANSFERRED, FROZEN, DISCARDED, THAWED, PENDING } = WELL_STATUS
            const { wellsByIndex: wells } = this.treatment

            const transferred = wells
                .filter(well => well.status === TRANSFERRED)
                .sort((a, b) => a.kidRank - b.kidRank)

            const thawed = wells
                .filter(well => well.status === THAWED)
                .sort((a, b) => a.kidRank - b.kidRank)

            const frozen = wells
                .filter(well => well.status === FROZEN)
                .sort((a, b) => a.kidRank - b.kidRank)

            const pending = wells
                .filter(well => well.status === PENDING)
                .sort((a, b) => a.kidRank - b.kidRank)

            const discarded = wells
                .filter(well => well.status === DISCARDED)
                .sort((a, b) => a.index - b.index)

            return [...transferred, ...thawed, ...frozen, ...pending, ...discarded]
        },
        pages() {
            const pages = [
                {
                    availableHeight: AVAILABLE_HEIGHT_BY_PAGE.firstPage,
                    wells: []
                }
            ]

            this.wells.forEach((well, idx) => {
                const currPage = pages.at(-1)
                const currWellHeight = CARD_HEIGHT[well.status]

                const isLastWell = idx === this.wells.length - 1
                const neededSpace =
                    isLastWell && this.showEqScore
                        ? currWellHeight + SCORE_EXPLAINED
                        : currWellHeight

                if (neededSpace <= currPage.availableHeight) {
                    currPage.wells.push(well)
                    currPage.availableHeight = currPage.availableHeight - neededSpace
                } else {
                    pages.push({
                        availableHeight: AVAILABLE_HEIGHT_BY_PAGE.default - neededSpace,
                        wells: [well]
                    })
                }
            })
            return pages
        },
        closingNeedsExtraPage() {
            const availableHeight = this.pages.at(-1).availableHeight

            return SIGNATURES_MIN_HEIGHT > availableHeight
        },
        totalPages() {
            return this.closingNeedsExtraPage ? this.pages.length + 1 : +this.pages.length
        },
        reportColorClass() {
            return this.tenant.color || 'black'
        },
        patientId() {
            return this.$utils.getPatientEsIdByVendor(
                this.treatment.instrumentType[0],
                this.patient
            )
        },
        treatmentId() {
            const treatmentDisplayIdsArray = this.treatment.slides.map(_slide => _slide.treatment.esId)
            const treatmentDisplayIdsSet = new Set(treatmentDisplayIdsArray)
            return [...treatmentDisplayIdsSet].join(', ')
        }
    },
    async created() {
        await this.setTreatmentData()
        this.setDefaultReportName()
    },
    methods: {
        async setTreatmentData() {
            UIUtils.showLoadingMask()

            const { treatment_id, patient_id } = this.$route.params

            await this.$store.dispatch('timelapse/loadPatientData', patient_id)
            await this.setLocale()
            await this.$store.dispatch('timelapse/setSelectedTreatment', treatment_id)
            UIUtils.hideLoadingMask()
        },
        setDefaultReportName() {
            this.reportName = this.$translate('cycle_report')
        },
        isLastPage(pageIndex) {
            return pageIndex === this.pages.length - 1
        },
        toggleEqScore() {
            this.showEqScore = !this.showEqScore
        },
        setNewReportName(newReportName) {
            this.reportName = newReportName
        },
        setLocale() {
            const { language } = this.tenant

            if (language) {
                this.$store.commit('locale/setLocale', language)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@page {
    size: A4;
    margin: 0;
}

.cycle-report {
    display: block;
    position: relative;
    padding-top: 100px;
    min-height: 100vh;
    color: $light-3;

    .print_content {
        position: relative;
        display: block;
        margin: 0 auto;
        width: 100%;
        min-height: 1122.519685px; // when using mm the result isn't consistent per screen

        @media print {
            width: 100%;
            height: 100%;
            margin: 0;
            padding: 0;
            page-break-before: always;
        }

        .embryo-development {
            .title {
                font-size: 18px;
                font-weight: 600;
                margin-bottom: 10px;
            }

            .embryo-development-list {
                list-style-type: none;
                padding: 0;
                margin: 0;

                li:not(:first-child) {
                    margin-top: 20px;
                }

                .embryo-development-card {
                    margin: 10px 0;
                }
            }
        }
    }

    @media print {
        background: none;
        color: black;
        margin: 0;
        padding: 0;

        * {
            -webkit-print-color-adjust: exact;
        }
    }
}
</style>
